'use client';

import { useEffect, useRef, useState, useTransition } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Bug,
  ChevronDown,
  Inbox,
  LifeBuoy,
  Lightbulb,
  Loader2,
  MessageSquareText,
  Paperclip,
  User,
  Users,
  X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useSupabase } from '@kit/supabase/hooks/use-supabase';
import { useUser } from '@kit/supabase/hooks/use-user';
import { Badge } from '@kit/ui/badge';
import { Button } from '@kit/ui/button';
import { toast } from '@kit/ui/sonner';
import { Spinner } from '@kit/ui/spinner';
import { Textarea } from '@kit/ui/textarea';
import { cn } from '@kit/ui/utils';

import { replyToBugReportAction } from '../../_lib/server/bug-report-actions';
import { compressImageIfNeeded } from './compress-image';
import { ReportAttachments, ReportDetails } from './report-details';
import {
  BUG_REPORT_ACCEPTED_MIME,
  type BugReportStatus,
  type BugReportType,
  getAttachmentExtension,
  REPORT_STATUS_LABELS,
  REPORT_TYPE_LABELS,
} from './report-labels';

// Reply attachments reuse the bug_reports bucket and the submission dialog's
// shared accepted-MIME list (images, MP4, PDF, Word docs), with a 5-file cap.
const REPLY_MAX_FILES = 5;
const REPLY_ACCEPTED_MIME: readonly string[] = BUG_REPORT_ACCEPTED_MIME;

interface MyReport {
  id: string;
  title: string;
  description: string;
  screenshots: string[];
  report_type: string;
  severity: string;
  status: string;
  admin_response: string | null;
  created_at: string;
  // Nullable: bug_reports.reporter_user_id is ON DELETE SET NULL.
  reporter_user_id: string | null;
}

interface ReportComment {
  id: string;
  report_id: string;
  actor_user_id: string | null;
  new_value: string | null;
  attachments: string[];
  created_at: string;
}

const REPORT_SELECT =
  'id, title, description, screenshots, report_type, severity, status, admin_response, created_at, reporter_user_id';

const STATUS_BADGE_VARIANTS = {
  new: 'neutral',
  in_progress: 'info',
  pending_release: 'info',
  reopened: 'warning',
  planned: 'info',
  future: 'neutral',
  done: 'success',
  declined: 'danger',
} as const satisfies Record<BugReportStatus, string>;

export function MyReportsList({
  accountId,
  highlightReportId,
}: {
  accountId: string;
  highlightReportId?: string | null;
}) {
  const supabase = useSupabase();
  const { data: user } = useUser();
  const { t } = useTranslation('common');
  const listRef = useRef<HTMLDivElement | null>(null);

  const [expandedId, setExpandedId] = useState<string | null>(
    highlightReportId ?? null,
  );

  // My own reports. Behaviour unchanged from before the "From my team" split —
  // a dedicated query keeps the 50-row cap scoped to MY reports so teammates'
  // activity can never crowd my own out of view.
  const { data: myReports, isLoading } = useQuery({
    queryKey: ['my-bug-reports', accountId, user?.id],
    enabled: Boolean(user?.id),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bug_reports')
        .select(REPORT_SELECT)
        .eq('account_id', accountId)
        .eq('reporter_user_id', user!.id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      return (data ?? []).map((report) => ({
        ...report,
        screenshots: Array.isArray(report.screenshots)
          ? report.screenshots.filter(
              (path): path is string => typeof path === 'string',
            )
          : [],
      }));
    },
  });

  // Teammates' reports for the same business. bug_reports_read already lets any
  // team member read every report on their tenant, so this is not a wider data
  // surface — it just surfaces what RLS already permits, in its own section.
  const { data: teamReports } = useQuery({
    queryKey: ['team-bug-reports', accountId, user?.id],
    enabled: Boolean(user?.id),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bug_reports')
        .select(REPORT_SELECT)
        .eq('account_id', accountId)
        .or(`reporter_user_id.neq.${user!.id},reporter_user_id.is.null`)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      return (data ?? []).map((report) => ({
        ...report,
        screenshots: Array.isArray(report.screenshots)
          ? report.screenshots.filter(
              (path): path is string => typeof path === 'string',
            )
          : [],
      }));
    },
  });

  // Reporter display names for the "From my team" section. accounts_read allows
  // a team member to read a teammate's personal-account name.
  const teamReporterIds = [
    ...new Set(
      (teamReports ?? [])
        .map((report) => report.reporter_user_id)
        .filter((id): id is string => Boolean(id)),
    ),
  ];
  const { data: reporterNameById } = useQuery({
    queryKey: ['bug-report-reporters', accountId, teamReporterIds.join(',')],
    enabled: teamReporterIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('accounts')
        .select('id, name')
        .in('id', teamReporterIds);

      if (error) throw error;

      const map = new Map<string, string>();
      for (const row of data ?? []) {
        if (row.name) map.set(row.id, row.name);
      }
      return map;
    },
  });

  const allReportIds = [...(myReports ?? []), ...(teamReports ?? [])].map(
    (report) => report.id,
  );

  // Conversation messages for every listed report in one query. RLS now returns
  // 'comment' events on any report of the caller's tenant (mine + teammates').
  const { data: comments } = useQuery({
    queryKey: ['bug-report-comments', accountId, user?.id, allReportIds.join(',')],
    enabled: allReportIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bug_report_events')
        .select('id, report_id, actor_user_id, new_value, attachments, created_at')
        .in('report_id', allReportIds)
        .eq('event_type', 'comment')
        .order('created_at', { ascending: true });

      if (error) throw error;

      // attachments is jsonb — coerce to a clean string[] for the UI.
      return (data ?? []).map((row) => ({
        ...row,
        attachments: Array.isArray(row.attachments)
          ? row.attachments.filter(
              (path): path is string => typeof path === 'string',
            )
          : [],
      })) as ReportComment[];
    },
  });

  const actorIds = [
    ...new Set(
      (comments ?? []).flatMap((comment) =>
        comment.actor_user_id ? [comment.actor_user_id] : [],
      ),
    ),
  ];
  const { data: actorNames } = useQuery({
    queryKey: ['bug-report-actors', accountId, actorIds.join(',')],
    enabled: actorIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('accounts')
        .select('id, name')
        .in('id', actorIds);
      if (error) throw error;
      return new Map((data ?? []).map((actor) => [actor.id, actor.name]));
    },
  });

  // Bring the deep-linked report into view once the list has rendered.
  useEffect(() => {
    if (!highlightReportId || (!myReports && !teamReports)) return;

    listRef.current
      ?.querySelector(`[data-report-id="${highlightReportId}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [highlightReportId, myReports, teamReports]);

  if (isLoading || !user) {
    return (
      <div className="flex justify-center py-10">
        <Spinner />
      </div>
    );
  }

  const mine = myReports ?? [];
  const team = teamReports ?? [];

  if (mine.length === 0 && team.length === 0) {
    return (
      <div className="text-muted-foreground flex flex-col items-center gap-2 py-10 text-center">
        <Inbox className="h-8 w-8" aria-hidden="true" />
        <p className="text-sm">
          {t('bugReport.myReports.empty', {
            defaultValue:
              "You haven't submitted anything yet. Bugs and feature requests you send will show up here with their status.",
          })}
        </p>
      </div>
    );
  }

  const commentsByReport = new Map<string, ReportComment[]>();

  for (const comment of comments ?? []) {
    const list = commentsByReport.get(comment.report_id) ?? [];
    list.push(comment);
    commentsByReport.set(comment.report_id, list);
  }

  const renderList = (reports: MyReport[]) => (
    <ul className="divide-border divide-y">
      {reports.map((report) => (
        <ReportItem
          key={report.id}
          report={report}
          comments={commentsByReport.get(report.id) ?? []}
          isExpanded={expandedId === report.id}
          onToggle={() =>
            setExpandedId(expandedId === report.id ? null : report.id)
          }
          reporterName={
            report.reporter_user_id === user.id
              ? null
              : (report.reporter_user_id
                  ? reporterNameById?.get(report.reporter_user_id)
                  : null) ?? null
          }
          currentUserId={user.id}
          accountId={accountId}
          actorNames={actorNames}
        />
      ))}
    </ul>
  );

  return (
    <div
      ref={listRef}
      className="space-y-8"
    >
      <section className="space-y-2">
        <SectionHeader
          icon={<User className="h-3.5 w-3.5" aria-hidden="true" />}
          title={t('bugReport.myReports.mineHeading', {
            defaultValue: 'My reports',
          })}
          count={mine.length}
        />

        {mine.length > 0 ? (
          renderList(mine)
        ) : (
          <p className="text-muted-foreground py-2 text-xs">
            {t('bugReport.myReports.mineEmpty', {
              defaultValue: "You haven't submitted anything yet.",
            })}
          </p>
        )}
      </section>

      {team.length > 0 ? (
        <section className="border-border space-y-2 border-t pt-4">
          <SectionHeader
            icon={<Users className="h-3.5 w-3.5" aria-hidden="true" />}
            title={t('bugReport.myReports.teamHeading', {
              defaultValue: 'From my team',
            })}
            count={team.length}
            subtitle={t('bugReport.myReports.teamSubheading', {
              defaultValue: 'View reports and reply together with your team',
            })}
          />

          {renderList(team)}
        </section>
      ) : null}
    </div>
  );
}

/**
 * Section label for the "My reports" / "From my team" split. An icon + count
 * badge (and an optional subtitle) give each section a scannable identity so the
 * two groups don't blur together. RTL-safe: `ps-*` mirrors and the flex row
 * flips automatically.
 */
function SectionHeader({
  icon,
  title,
  count,
  subtitle,
}: {
  icon: React.ReactNode;
  title: string;
  count: number;
  subtitle?: string;
}) {
  return (
    <div className="space-y-1">
      <div className="text-foreground flex items-center gap-2">
        <span className="text-muted-foreground" aria-hidden="true">
          {icon}
        </span>
        <h3 className="text-lg font-semibold">
          {title}
        </h3>
        {count > 0 ? (
          // Neutral count pill: bg-muted is a plain gray in every tenant theme
          // (never brand-tinted), so it stays simple and legible in light + dark.
          <span className="bg-muted text-foreground inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-medium tabular-nums">
            {count}
          </span>
        ) : null}
      </div>
      {subtitle ? (
        <p className="text-muted-foreground ps-[1.375rem] text-sm">
          {subtitle}
        </p>
      ) : null}
    </div>
  );
}

/** One report card — used in both the "My reports" and "From my team" sections. */
function ReportItem({
  report,
  comments,
  isExpanded,
  onToggle,
  reporterName,
  currentUserId,
  accountId,
  actorNames,
}: {
  report: MyReport;
  comments: ReportComment[];
  isExpanded: boolean;
  onToggle: () => void;
  reporterName?: string | null;
  currentUserId: string;
  accountId: string;
  actorNames?: Map<string, string | null>;
}) {
  const { t } = useTranslation('common');

  const status = (
    report.status in STATUS_BADGE_VARIANTS ? report.status : 'new'
  ) as BugReportStatus;

  const isFeature = report.report_type === 'feature_request';
  const isSupport = report.report_type === 'support_request';
  const type = (isFeature
    ? 'feature_request'
    : isSupport
      ? 'support_request'
      : 'bug') as BugReportType;

  return (
    <li
      data-report-id={report.id}
      className="min-w-0 py-5 first:pt-3"
    >
      <div className="flex flex-col items-start justify-between gap-2 sm:flex-row sm:gap-4">
        <div className="flex min-w-0 items-start gap-2">
          {isFeature ? (
            <Lightbulb
              className="text-muted-foreground h-4 w-4 shrink-0"
              aria-hidden="true"
            />
          ) : isSupport ? (
            <LifeBuoy
              className="text-muted-foreground h-4 w-4 shrink-0"
              aria-hidden="true"
            />
          ) : (
            <Bug
              className="text-muted-foreground h-4 w-4 shrink-0"
              aria-hidden="true"
            />
          )}
          <h4 className="min-w-0 text-base leading-snug font-semibold [overflow-wrap:anywhere] sm:text-lg">
            {report.title}
          </h4>
        </div>
        <Badge
          variant={STATUS_BADGE_VARIANTS[status]}
          className="shrink-0"
        >
          {t(`bugReport.status.${status}`, {
            defaultValue: REPORT_STATUS_LABELS[status],
          })}
        </Badge>
      </div>

      <div className="text-muted-foreground mt-2 text-sm [overflow-wrap:anywhere]">
        {t(
          type === 'bug'
            ? 'bugReport.type.bug.label'
            : type === 'support_request'
              ? 'bugReport.type.support.label'
              : 'bugReport.type.feature.label',
          { defaultValue: REPORT_TYPE_LABELS[type] },
        )}{' '}
        · {new Date(report.created_at).toLocaleDateString()}
        {reporterName ? (
          <>
            {' '}
            ·{' '}
            {t('bugReport.myReports.reportedBy', {
              defaultValue: 'Reported by {{name}}',
              name: reporterName,
            })}
          </>
        ) : null}
      </div>

      <button
        type="button"
        className="text-muted-foreground hover:text-foreground focus-visible:ring-ring mt-3 flex min-h-11 items-center gap-2 rounded-sm text-sm font-medium focus-visible:ring-2 focus-visible:outline-none"
        onClick={onToggle}
        aria-expanded={isExpanded}
        aria-controls={`report-details-${report.id}`}
      >
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 transition-transform',
            isExpanded && 'rotate-180',
          )}
          aria-hidden="true"
        />
        {comments.length > 0
          ? t('bugReport.myReports.detailsWithCount', {
              defaultValue: 'Details & conversation ({{count}})',
              count: comments.length,
            })
          : t('bugReport.myReports.details', {
              defaultValue: 'View report details',
            })}
      </button>

      {isExpanded ? (
        <div
          id={`report-details-${report.id}`}
          className="mt-3 space-y-6 border-t pt-5"
        >
          <ReportDetails report={report} />
          {report.admin_response ? (
            <section className="space-y-2">
              <h5 className="text-sm font-semibold">
                {t('bugReport.myReports.supportResponse', {
                  defaultValue: 'Support response',
                })}
              </h5>
              <p
                dir="auto"
                className="bg-muted/40 rounded-lg p-4 text-base leading-relaxed whitespace-pre-wrap [overflow-wrap:anywhere]"
              >
                {report.admin_response}
              </p>
            </section>
          ) : null}
          <ReportThread
            accountId={accountId}
            reportId={report.id}
            comments={comments}
            currentUserId={currentUserId}
            reporterUserId={report.reporter_user_id}
            reporterName={reporterName}
            actorNames={actorNames}
          />
        </div>
      ) : null}
    </li>
  );
}

/**
 * The back-and-forth thread with the AskAI team for one report. Messages are
 * 'comment' rows in bug_report_events; replies go through
 * replyToBugReportAction (service-role insert, gated on tenant membership).
 */
function ReportThread({
  accountId,
  reportId,
  comments,
  currentUserId,
  reporterUserId,
  reporterName,
  actorNames,
}: {
  accountId: string;
  reportId: string;
  comments: ReportComment[];
  currentUserId: string;
  // The report's own reporter — used to attribute their messages. In the "From
  // my team" section the reporter is a teammate (not the viewer), so their
  // comments must show their name, not "AskAI Support".
  reporterUserId: string | null;
  reporterName?: string | null;
  actorNames?: Map<string, string | null>;
}) {
  const { t } = useTranslation('common');
  const supabase = useSupabase();
  const queryClient = useQueryClient();
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [isSending, startTransition] = useTransition();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const attachmentPaths = comments.flatMap((comment) => comment.attachments);

  const addFiles = (list: FileList | null) => {
    if (!list) return;

    const selected = Array.from(list);
    const accepted = selected.filter((file) =>
      REPLY_ACCEPTED_MIME.includes(file.type),
    );

    // Explain missing chips instead of silently dropping files: unsupported
    // MIME types are filtered out and anything past the cap is sliced. Computed
    // from the `files` closure (not inside the setState updater) so a StrictMode
    // double-invoke can't fire the toast twice.
    const rejected = selected.length - accepted.length;
    const combined = [...files, ...accepted];

    if (rejected > 0) {
      toast.error(
        t('bugReport.thread.attachmentTypeRejected', {
          defaultValue: 'Some files were skipped — unsupported type',
        }),
      );
    }

    if (combined.length > REPLY_MAX_FILES) {
      toast.error(
        t('bugReport.thread.attachmentLimit', {
          max: REPLY_MAX_FILES,
          defaultValue: 'You can attach up to {{max}} files',
        }),
      );
    }

    setFiles(combined.slice(0, REPLY_MAX_FILES));

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const onSend = () => {
    const trimmed = message.trim();
    if (!trimmed && files.length === 0) return;

    // Hoisted out of the try so the catch can clean up whatever already landed.
    const uploadedPaths: string[] = [];

    startTransition(async () => {
      try {
        // Upload attachments to THIS report's own folder first; the server
        // action re-checks every path is bound to {account_id}/{report_id}/.
        // Sequential (not Promise.all) on purpose: a failed upload attributes
        // cleanly to one file, and uploadedPaths then holds exactly what landed
        // so the catch can remove precisely those objects. File count is capped
        // at REPLY_MAX_FILES, so there is no meaningful latency win from parallel.
        for (const raw of files) {
          // compressImageIfNeeded is a no-op for non-image types (video/pdf/doc
          // pass through unchanged), so they upload as-is. Derive the extension
          // from the (possibly compressed) file so a .docx/.pdf keeps its real
          // suffix instead of being mislabeled .jpg.
          const file = await compressImageIfNeeded(raw);
          const extension = getAttachmentExtension(file);
          const path = `${accountId}/${reportId}/${crypto.randomUUID()}${extension}`;

          const { error } = await supabase.storage
            .from('bug_reports')
            .upload(path, file, { upsert: false, contentType: file.type });

          if (error) throw error;
          uploadedPaths.push(path);
        }

        const result = await replyToBugReportAction({
          reportId,
          message: trimmed,
          attachments: uploadedPaths,
        });

        if (!result?.success) {
          throw new Error('Failed to send reply');
        }

        setMessage('');
        setFiles([]);
        void queryClient.invalidateQueries({
          queryKey: ['bug-report-comments', accountId],
        });
      } catch {
        // Best-effort cleanup: drop any objects uploaded before the failure so a
        // partial upload or rejected action doesn't leave orphans in the bucket.
        // Fire-and-forget — a cleanup error must not mask the original one.
        if (uploadedPaths.length > 0) {
          void supabase.storage.from('bug_reports').remove(uploadedPaths);
        }

        toast.error(
          t('bugReport.thread.sendFailed', {
            defaultValue: 'Failed to send your reply. Please try again.',
          }),
        );
      }
    });
  };

  return (
    <div className="mt-3 space-y-4 border-t pt-4">
      <h5 className="flex items-center gap-2 text-base font-semibold">
        <MessageSquareText className="h-4 w-4" aria-hidden="true" />
        {t('bugReport.thread.toggle', { defaultValue: 'Conversation' })}
      </h5>
      {comments.length > 0 ? (
        <ul className="space-y-4">
          {comments.map((comment) => {
            const isMine = comment.actor_user_id === currentUserId;
            const isReporter =
              !isMine &&
              comment.actor_user_id != null &&
              comment.actor_user_id === reporterUserId;
            const actorName = comment.actor_user_id
              ? actorNames?.get(comment.actor_user_id)
              : null;
            const authorLabel = isMine
              ? t('bugReport.thread.you', { defaultValue: 'You' })
              : actorName ||
                (isReporter
                  ? (reporterName ??
                    t('bugReport.thread.teammate', { defaultValue: 'Teammate' }))
                  : t('bugReport.thread.participant', {
                      defaultValue: 'Participant',
                    }));

            return (
              <li
                key={comment.id}
                className={cn(
                  'max-w-full rounded-lg p-3 text-base leading-relaxed whitespace-pre-wrap [overflow-wrap:anywhere] sm:max-w-[90%] sm:p-4',
                  isMine ? 'bg-muted ms-auto' : 'bg-background me-auto',
                )}
              >
                <div className="text-muted-foreground mb-2 text-sm">
                  {authorLabel} ·{' '}
                  {new Date(comment.created_at).toLocaleString()}
                </div>
                {comment.new_value}
                {comment.attachments.length > 0 && (
                  <div className="mt-3">
                    <ReportAttachments
                      reportId={reportId}
                      paths={comment.attachments}
                      signingPaths={attachmentPaths}
                    />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-muted-foreground text-xs">
          {t('bugReport.thread.empty', {
            defaultValue:
              'No messages yet. Replies from our team will appear here.',
          })}
        </p>
      )}

        <div className="space-y-1.5">
          <Textarea
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            placeholder={t('bugReport.thread.placeholder', {
              defaultValue: 'Write a reply…',
            })}
            aria-label={t('bugReport.thread.placeholder', {
              defaultValue: 'Write a reply…',
            })}
            className="min-h-24 text-base leading-relaxed md:text-base"
            maxLength={5_000}
            disabled={isSending}
          />

          {files.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {files.map((file, index) => (
                <span
                  key={`${file.name}-${index}`}
                  className="bg-muted inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10px]"
                >
                  <span className="max-w-[140px] truncate">{file.name}</span>
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-destructive"
                    onClick={() =>
                      setFiles((prev) => prev.filter((_, i) => i !== index))
                    }
                    disabled={isSending}
                    aria-label={t('bugReport.thread.removeAttachment', {
                      defaultValue: 'Remove attachment',
                    })}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept={REPLY_ACCEPTED_MIME.join(',')}
            multiple
            className="hidden"
            onChange={(event) => addFiles(event.target.files)}
          />

          <div className="flex items-center justify-between">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={isSending || files.length >= REPLY_MAX_FILES}
            >
              <Paperclip className="mr-1 h-3.5 w-3.5" />
              {t('bugReport.thread.attach', { defaultValue: 'Attach' })}
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={onSend}
              disabled={isSending || (!message.trim() && files.length === 0)}
            >
              {isSending ? (
                <>
                  <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                  {t('bugReport.thread.sending', { defaultValue: 'Sending…' })}
                </>
              ) : (
                t('bugReport.thread.send', { defaultValue: 'Send reply' })
              )}
            </Button>
          </div>
        </div>
    </div>
  );
}
